import type { AudioEngine } from './AudioEngine.js';

export interface EngineSoundOptions {
  cylinders?: number;
  idleRpm?: number;
  redlineRpm?: number;
}

/**
 * Two-layer procedural engine synth.
 *
 *   osc A (smooth) ──→ smoothOscGain (RPM only) ─┐
 *                                                ├─→ lpf ──→ out ──→ master
 *   osc B (rough)  ──→ roughOscGain  (RPM×thr.) ─┤   cutoff = RPM + throttle
 *                                                │
 *   noise → bandpass ──→ noiseGain (RPM + thr.) ─┘
 *
 * Smooth osc carries the always-on engine baseline (no throttle dependence,
 * so volume doesn't jump on key press). Rough osc fades in under load and
 * carries the harmonic-rich "growl". Filter cutoff and combustion noise also
 * open up with throttle for a multi-dimensional load shift.
 */
export class EngineSound {
  private ctx: AudioContext;
  private oscA: OscillatorNode;
  private oscB: OscillatorNode;
  private smoothOscGain: GainNode;
  private roughOscGain: GainNode;
  private noise: AudioBufferSourceNode;
  private noiseBp: BiquadFilterNode;
  private noiseGain: GainNode;
  private lpf: BiquadFilterNode;
  private out: GainNode;
  private volumeGain: GainNode;
  private volume = 1.0;
  private muteGain: GainNode;
  private muted = false;
  /** Ignition state — false until the player first presses any engine input
   *  (forward throttle or reverse pedal). See the gate logic in update(). */
  private running = false;
  private cylinders: number;
  private idle: number;
  private redline: number;
  private started = false;
  private smoothedThrottle = 0;

  constructor(audio: AudioEngine, noiseBuf: AudioBuffer, opts: EngineSoundOptions = {}) {
    this.ctx = audio.ctx;
    this.cylinders = opts.cylinders ?? 4;
    this.idle = opts.idleRpm ?? 800;
    this.redline = opts.redlineRpm ?? 7000;

    // --- osc A: smooth/coast layer ---
    // Hand-tuned harmonic spectrum — softer high end so coasting feels rounder.
    const smoothReal = new Float32Array([0, 0.6, 1.0, 0.5, 0.7, 0.25, 0.4, 0.15, 0.2, 0.08, 0.1]);
    const smoothImag = new Float32Array(smoothReal.length);
    const smoothWave = this.ctx.createPeriodicWave(smoothReal, smoothImag, {
      disableNormalization: false,
    });

    this.oscA = this.ctx.createOscillator();
    this.oscA.setPeriodicWave(smoothWave);
    this.oscA.frequency.value = this.firingFreq(this.idle);

    this.smoothOscGain = this.ctx.createGain();
    this.smoothOscGain.gain.value = 0.05;

    // --- osc B: rough/load layer ---
    // Same fundamental, but upper harmonics are much stronger. Fades in with
    // throttle to add growl/bite without pulling the smooth baseline around.
    const roughReal = new Float32Array([0, 0.5, 1.0, 0.85, 0.95, 0.55, 0.7, 0.4, 0.5, 0.3, 0.4]);
    const roughImag = new Float32Array(roughReal.length);
    const roughWave = this.ctx.createPeriodicWave(roughReal, roughImag, {
      disableNormalization: false,
    });

    this.oscB = this.ctx.createOscillator();
    this.oscB.setPeriodicWave(roughWave);
    this.oscB.frequency.value = this.firingFreq(this.idle);

    this.roughOscGain = this.ctx.createGain();
    this.roughOscGain.gain.value = 0.0; // starts silent — fades in with throttle

    // --- noise: combustion / intake / exhaust grit ---
    this.noise = this.ctx.createBufferSource();
    this.noise.buffer = noiseBuf;
    this.noise.loop = true;

    this.noiseBp = this.ctx.createBiquadFilter();
    this.noiseBp.type = 'bandpass';
    this.noiseBp.frequency.value = this.firingFreq(this.idle) * 4;
    this.noiseBp.Q.value = 1.5;

    this.noiseGain = this.ctx.createGain();
    this.noiseGain.gain.value = 0.02;

    // --- shared LPF: cutoff opens with RPM + throttle ---
    this.lpf = this.ctx.createBiquadFilter();
    this.lpf.type = 'lowpass';
    this.lpf.frequency.value = 400;
    this.lpf.Q.value = 0.7;

    // --- master gate: 0 when stopped+no-throttle, 1 otherwise ---
    this.out = this.ctx.createGain();
    this.out.gain.value = 0.0;

    // --- user volume gain (Settings → Sounds slider, persisted) ---
    this.volumeGain = this.ctx.createGain();
    this.volumeGain.gain.value = this.volume;

    // --- user mute gain (separate from the gate so they don't fight) ---
    this.muteGain = this.ctx.createGain();
    this.muteGain.gain.value = 1.0;

    // Wiring: both oscs and noise sum into the LPF, then through the gate,
    // then the user volume, then the user mute, then out to master.
    this.oscA.connect(this.smoothOscGain).connect(this.lpf);
    this.oscB.connect(this.roughOscGain).connect(this.lpf);
    this.noise.connect(this.noiseBp).connect(this.noiseGain).connect(this.lpf);
    this.lpf
      .connect(this.out)
      .connect(this.volumeGain)
      .connect(this.muteGain)
      .connect(audio.master);
  }

  setVolume(v: number): void {
    this.volume = clamp01(v);
    this.volumeGain.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.05);
  }

  getVolume(): number {
    return this.volume;
  }

  setMuted(m: boolean): void {
    this.muted = m;
    this.muteGain.gain.setTargetAtTime(m ? 0 : 1, this.ctx.currentTime, 0.05);
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  isMuted(): boolean {
    return this.muted;
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.oscA.start();
    this.oscB.start();
    // Random offset into the shared noise buffer so this source is uncorrelated
    // with the other layers' noise sources (wind whoosh/rumble, tire). Without
    // this, three sources all reading the same 2 s of samples from offset 0
    // would create audible comb filtering when their bandpasses overlap.
    const noiseOffset = Math.random() * (this.noise.buffer?.duration ?? 0);
    this.noise.start(0, noiseOffset);
    // `out` acts as the engine on/off gate, driven by update(). Stays at 0 until
    // the car is moving or the throttle is pressed.
  }

  /**
   * Call every frame. RPM drives pitch + baseline volume. `engineInput` is the
   * player's engine-command magnitude in [0, 1] — forward throttle OR reverse
   * pedal when actually reversing (Vehicle.getEngineInput()). It drives the
   * crossfade into the rough/load layer plus filter brightness and noise gain —
   * but never the smooth osc's gain, so volume doesn't step on key press.
   * `speed` is the chassis linear speed in m/s; used only by the ignition gate.
   */
  update(rpm: number, speed: number, engineInput: number): void {
    if (!this.started) return;
    const now = this.ctx.currentTime;
    const TAU_FAST = 0.02;
    const TAU_SLOW = 0.05;
    const TAU_GATE = 0.1;

    // Local engine-input smoothing — ~0.1 s rise time. Prevents zipper noise and
    // the click-feel of instantaneous gain swings on key press/release.
    this.smoothedThrottle += (engineInput - this.smoothedThrottle) * 0.12;
    const t = this.smoothedThrottle;

    // Pitch — both oscs run at the same firing frequency.
    const freq = this.firingFreq(rpm);
    this.oscA.frequency.setTargetAtTime(freq, now, TAU_FAST);
    this.oscB.frequency.setTargetAtTime(freq, now, TAU_FAST);
    this.noiseBp.frequency.setTargetAtTime(freq * 4, now, TAU_SLOW);

    const rpmNorm = clamp01((rpm - this.idle) / (this.redline - this.idle));

    // Filter cutoff — the single biggest perceptual cue for load vs coast.
    // Heavy throttle weighting: coasting at redline ≈ 950 Hz (muffled rumble);
    // full throttle at redline ≈ 3950 Hz (bright/open). The big sweep is what
    // makes the throttle on/off contrast easy to hear.
    const cutoff = 350 + rpmNorm * 600 + t * 3000;
    this.lpf.frequency.setTargetAtTime(cutoff, now, TAU_SLOW);

    // Smooth osc: RPM only. Carries the always-on baseline — prevents volume jumps.
    // Boosted because the heavy LPF sweep at coast otherwise muffles the engine too
    // much; this restores audible coasting weight without affecting load contrast.
    this.smoothOscGain.gain.setTargetAtTime(0.10 + rpmNorm * 0.40, now, TAU_SLOW);

    // Rough osc: throttle × RPM. Zero coasting → "dull"; rises strongly under load → "growl".
    this.roughOscGain.gain.setTargetAtTime(t * (0.06 + rpmNorm * 0.30), now, TAU_SLOW);

    // Combustion noise: low coast floor, big throttle add for intake/exhaust roar.
    this.noiseGain.gain.setTargetAtTime(0.01 + rpmNorm * 0.05 + t * 0.12, now, TAU_SLOW);

    // --- Ignition state machine ---
    //
    // OFF → ON : the player presses ANY engine input — forward throttle OR
    //            reverse pedal when actually reversing. "Starts the engine."
    // ON → OFF : the car has fully stopped (speed < 2 km/h) AND the player is
    //            off all engine input. Once off, only a new engine-input press
    //            re-ignites — sliding under gravity does not.
    //
    // Replaces the previous stateless `throttle > 0.01 || speed > 0.5` gate,
    // which falsely activated on teleport-fall, hill-sliding, and any other
    // physics-driven motion before the player had actually started driving.
    const ENGINE_ON = 0.05;
    const ENGINE_OFF = 0.01;
    const STOP_SPEED_MS = 2 / 3.6; // 2 km/h
    if (!this.running) {
      if (engineInput > ENGINE_ON) this.running = true;
    } else {
      if (engineInput < ENGINE_OFF && speed < STOP_SPEED_MS) this.running = false;
    }
    this.out.gain.setTargetAtTime(this.running ? 1 : 0, now, TAU_GATE);
  }

  /** 4-stroke: rpm/60 × cylinders/2. */
  private firingFreq(rpm: number): number {
    return (rpm / 60) * (this.cylinders / 2);
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
